import React from "react";
import { Link, useLocation } from "react-router-dom";
import { LOGO } from "@/lib/brand";
import { DIVISION_COLOR } from "@/lib/divisions";
import { SITE } from "@/constants/testIds";

const DIVISION_LINES = [
  { name: "Relatable Studio",     color: DIVISION_COLOR.Studio },
  { name: "Relatable Marketing",  color: DIVISION_COLOR.Marketing },
  { name: "Relatable Experience", color: DIVISION_COLOR.Experience },
];

export default function Footer() {
  const loc = useLocation();
  if (loc.pathname.startsWith("/admin")) return null;
  // Contact page already has the full contact form — skip the CTA half of the footer there
  const hideCta = loc.pathname.startsWith("/contact");
  return (
    <footer data-testid={SITE.footer} className="bg-ink text-bone border-t border-line">
      <div className="px-6 md:px-12 lg:px-24 pt-20 md:pt-24 pb-10">
        {!hideCta && (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-7">
            <p className="eyebrow text-fog mb-6">Let&rsquo;s talk →</p>
            <h2 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter font-medium leading-[0.95]">
              Tell us the<br/>
              <span className="text-relatable">problem first.</span>
            </h2>
            <p className="mt-8 max-w-xl text-fog text-base md:text-lg leading-relaxed">
              We&rsquo;ll figure out whether it needs a film, a campaign, a content series, or none of the above.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/contact" className="px-6 py-4 bg-bone text-ink eyebrow hover:bg-relatable hover:text-bone transition-colors">
                Book A Discovery Call →
              </Link>
              <Link to="/contact" className="px-6 py-4 border border-line text-bone eyebrow hover:border-bone transition-colors">
                Start A Project
              </Link>
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="eyebrow text-fog mb-6">Studio</p>
            <ul className="space-y-3 text-bone/90">
              <li><Link to="/work" className="link-line">Work</Link></li>
              <li><Link to="/about" className="link-line">About</Link></li>
              <li><Link to="/contact" className="link-line">Contact</Link></li>
              <li><a href="mailto:nexa@relatable.studio" className="link-line" data-testid="footer-email">nexa@relatable.studio</a></li>
              <li><a href="https://wa.me/919380021206?text=Hi%20Relatable%20Studio%20%E2%80%94%20I%27d%20like%20to%20discuss%20a%20project." target="_blank" rel="noopener noreferrer" className="link-line" data-testid="footer-whatsapp">WhatsApp · +91 93800 21206</a></li>
            </ul>
            <p className="eyebrow text-fog mt-8 mb-4">Follow</p>
            <ul className="space-y-3 text-bone/90">
              <li><a href="https://www.instagram.com/studiorelatable/" target="_blank" rel="noopener noreferrer me" className="link-line" data-testid="footer-instagram">Instagram</a></li>
              <li><a href="https://www.linkedin.com/company/relatablestudio/" target="_blank" rel="noopener noreferrer me" className="link-line" data-testid="footer-linkedin">LinkedIn</a></li>
              <li><a href="https://vimeo.com/relatablestudio" target="_blank" rel="noopener noreferrer me" className="link-line" data-testid="footer-vimeo">Vimeo</a></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="eyebrow text-fog mb-4">Registered Office</p>
            <p className="text-bone/90 leading-relaxed not-italic">
              Whitefield<br/>
              Bengaluru, Karnataka
            </p>
            <p className="mt-1 text-fog text-xs">Registered business address</p>

            <p className="mt-8 eyebrow text-fog mb-4">Bengaluru HQ</p>
            <p className="text-bone/90 leading-relaxed not-italic">
              BHIVE Platinum<br/>
              Church Street, Bengaluru<br/>
              Karnataka
            </p>
            <p className="mt-1 text-fog text-xs">Available by appointment</p>

            <p className="mt-8 eyebrow text-fog">Heritage</p>
            <p className="text-bone/90 leading-relaxed mt-2">
              Formerly <span className="text-bone">Straight Arrow Studios</span><br/>
              <span className="text-fog">Est. 2020 · Rebranded 2026</span>
            </p>
          </div>
        </div>
        )}

        {/* Resources — long-form pillar & comparison articles for topical authority. */}
        <div className="mt-14 md:mt-16 pt-8 border-t border-line">
          <p className="eyebrow text-fog mb-4">Resources</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6 text-bone/90">
            <Link
              to="/brand-film-cost-india"
              data-testid="footer-resource-cost"
              className="link-line block text-sm md:text-base"
            >
              Brand film cost in India →
            </Link>
            <Link
              to="/relatable-vs-ad-agency"
              data-testid="footer-resource-vs-agency"
              className="link-line block text-sm md:text-base"
            >
              Studio vs ad agency →
            </Link>
            <Link
              to="/relatable-vs-freelance-crew"
              data-testid="footer-resource-vs-freelance"
              className="link-line block text-sm md:text-base"
            >
              Studio vs freelance crew →
            </Link>
            <Link
              to="/in-house-vs-external-production"
              data-testid="footer-resource-inhouse"
              className="link-line block text-sm md:text-base"
            >
              In-house vs external →
            </Link>
          </div>
        </div>

        {/* Divisions — coloured */}
        <div className="mt-16 md:mt-20 pt-10 border-t border-line">
          <p className="eyebrow text-fog mb-6">Three Divisions · One Studio</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
            {DIVISION_LINES.map((d) => (
              <div key={d.name} className="flex items-center gap-3">
                <span className="block w-2.5 h-2.5" style={{ backgroundColor: d.color }} />
                <span className="font-display text-xl md:text-2xl tracking-tight" style={{ color: d.color }}>
                  {d.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Massive logo lockup */}
        <div className="mt-16 md:mt-20 border-t border-line pt-10">
          <img
            src={LOGO.type_white}
            alt="RELATABLE"
            className="w-full h-auto select-none opacity-95"
            draggable={false}
          />
        </div>

        <div className="mt-10 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="eyebrow text-fog">© {new Date().getFullYear()} RELATABLE</p>
          <p className="eyebrow text-fog">Registered · Whitefield &nbsp;·&nbsp; Bengaluru HQ · Church Street</p>
        </div>
      </div>
    </footer>
  );
}
